import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavBar } from '@/components/NavBar';
import { BottomNav } from '@/components/BottomNav';
import { OfflineBanner } from '@/components/OfflineBanner';
import { ChevronLeft, MoreVertical, LogOut, Shield, Bug, Download, Share, X, Sun, Moon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import { ReportIssueModal } from '@/components/ReportIssueModal';
import { useInstall } from '@/contexts/InstallContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeSelector } from '@/components/ThemeSelector';
import { ThemeBackdrop } from '@/components/ThemeBackdrop';
import { SwipeNavigator } from '@/components/SwipeNavigator';

// Page titles for the mobile header
const PAGE_TITLES = {
  '/dashboard': 'Training Hub',
  '/workout': 'My Program',
  '/chat': 'Coach',
  '/achievements': 'Awards',
  '/settings': 'Settings',
  '/analytics': 'Progress',
  '/progression': 'Level',
  '/exercises': 'Exercise Library',
  '/subscribe': 'Subscribe',
};

function MobileHeader() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);
  const [showIOSHelper, setShowIOSHelper] = useState(false);
  const { canInstallNative, isIOSSafari, installed, triggerInstall } = useInstall();

  // Hide on landing, auth, onboarding, active-workout
  const hiddenPaths = ['/', '/login', '/register', '/onboarding', '/forgot-password', '/reset-password'];
  if (hiddenPaths.includes(location.pathname) || location.pathname.startsWith('/active-workout')) return null;

  const title = PAGE_TITLES[location.pathname] || 
    (location.pathname.startsWith('/workout/') ? 'My Program' : 
     location.pathname.startsWith('/blog') ? 'Blog' : 'BODYMAX');

  const showBack = !['/dashboard', '/workout', '/chat', '/achievements', '/settings'].includes(location.pathname);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 md:hidden backdrop-blur-xl"
        style={{ background: 'rgba(10,10,10,0.95)', paddingTop: 'env(safe-area-inset-top, 0px)' }}
        data-testid="mobile-header"
      >
        <div className="flex items-center justify-between h-14 px-5">
          <div className="flex items-center gap-2 min-w-[44px]">
          {showBack ? (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 -ml-1 rounded-full active:scale-95 transition-transform"
              data-testid="mobile-back-btn"
            >
              <ChevronLeft size={22} className="text-muted-foreground" />
            </button>
          ) : (
            <span className="font-heading text-xl font-black tracking-tight text-white" data-testid="mobile-logo">BODYMAX<span className="text-accent">.FIT</span></span>
          )}
          </div>

        <h1 className="font-heading text-base font-semibold tracking-tight text-foreground truncate">
          {showBack ? title : ''}
        </h1>

        <div className="min-w-[80px] flex justify-end items-center gap-1">
          <ThemeSelector className="w-10 h-10 border border-white/15 bg-white/5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/5 active:scale-95 transition-transform" data-testid="mobile-menu-btn">
                <MoreVertical size={18} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-muted-foreground cursor-default">
                {user?.email}
              </DropdownMenuItem>
              {user?.is_dev && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="text-accent">
                    <Shield size={14} className="mr-2" /> Admin
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowReportModal(true)}>
                <Bug size={14} className="mr-2" /> Report Issue
              </DropdownMenuItem>
              {!installed && (canInstallNative || isIOSSafari) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => {
                    if (canInstallNative) {
                      await triggerInstall();
                    } else if (isIOSSafari) {
                      setShowIOSHelper(true);
                    }
                  }} data-testid="menu-install-app">
                    <Download size={14} className="mr-2" /> Install App
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate('/'); }}>
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>
        {/* Full-width green accent line */}
        <div className="h-[3px] w-full bg-accent" />
      </header>
      <ReportIssueModal open={showReportModal} onOpenChange={setShowReportModal} />

      {/* iOS Add to Home Screen helper sheet */}
      {showIOSHelper && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setShowIOSHelper(false)}>
          <div className="absolute inset-0 bg-background/60" />
          <div
            className="relative w-full max-w-md mx-3 mb-4 rounded-2xl overflow-hidden"
            style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={e => e.stopPropagation()}
            data-testid="ios-install-helper"
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-base font-bold text-foreground">Add BODYMAX to Home Screen</h3>
              <button onClick={() => setShowIOSHelper(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 active:scale-90">
                <X size={16} className="text-muted-foreground" />
              </button>
            </div>
            <div className="px-5 pb-5">
              <p className="text-sm text-muted-foreground mb-4">
                For the best app-like experience on iPhone, add BODYMAX to your Home Screen from Safari.
              </p>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 mb-4">
                <Share size={20} className="text-accent shrink-0" />
                <p className="text-sm text-foreground">
                  Tap <strong>Share</strong> <Share size={12} className="inline -mt-0.5 mx-0.5 text-muted-foreground" /> then <strong>Add to Home Screen</strong>
                </p>
              </div>
              <button
                onClick={() => setShowIOSHelper(false)}
                className="w-full py-3 rounded-xl bg-white text-black text-sm font-semibold active:scale-[0.98] transition-transform"
                data-testid="ios-helper-gotit"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function AppLayout({ children }) {
  const { user, api } = useAuth();
  const location = useLocation();

  // No layout for public pages, auth, onboarding
  const noLayoutPaths = ['/', '/login', '/register', '/onboarding', '/forgot-password', '/reset-password', '/confirm-email'];
  const isPublicPage = noLayoutPaths.includes(location.pathname) || 
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/legal') ||
    location.pathname === '/privacy' || location.pathname === '/privacy-policy' ||
    location.pathname === '/terms' || location.pathname === '/delete-account' ||
    location.pathname === '/faqs' || location.pathname === '/support';

  const isPaywall = location.pathname === '/subscribe';

  // Themed background image: Home + Program only
  const showThemeBackdrop = location.pathname === '/dashboard' || location.pathname === '/workout';

  if (!user || isPublicPage) {
    return <>{children}</>;
  }

  if (isPaywall) {
    // Full screen — no nav bars
    return <>{children}</>;
  }

  return (
    <>
      <OfflineBanner api={api} />
      {showThemeBackdrop && <ThemeBackdrop />}
      {/* Desktop: top nav. Mobile: compact header */}
      <div className="hidden md:block">
        <NavBar />
      </div>
      <div className="md:hidden">
        <MobileHeader />
      </div>

      {/* Page content with bottom nav spacing */}
      <main className="pb-bottom-nav md:pb-0 pt-14 md:pt-0">
        <SwipeNavigator>{children}</SwipeNavigator>
      </main>

      {/* Mobile bottom nav */}
      <div className="md:hidden">
        <BottomNav />
      </div>
    </>
  );
}
